import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { Tabs, Tab } from "@mui/material";
import { BackButton } from "src/common/components/BackButton";
import { useAccess } from "src/hooks/useAccess.hook";
import sidebarConfig from "src/layouts/dashboard/SidebarConfig";
import { alertSlice } from "src/store/alert/alert.slice";
import { FEATURE } from "src/util/enums";
import Page from "../Page";
import { PaymentGatewayForm } from "./PaymentGatewayForm";
import { PaymentGatewayTransactions } from "./PaymentGatewayTransactions";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";

export const PaymentGateway = () => {
  const navigate = useNavigate();
  const isCreatePaymentGateway = useMatch("/payment-gateways/new");
  const dispatch = useDispatch();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState("details");
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const isGrandParent = useIsGrandParent();
  const paymentGatewayViewAccess = useAccess(FEATURE.PAYMENTGATEWAY, "V");
  const paymentGatewayCreateAccess = useAccess(FEATURE.PAYMENTGATEWAY, "C");

  // Check if Grand Parent Company is selected (teamId matches user.company.id)
  const isGrandParentCompanySelected = useMemo(() => {
    return String(teamId) === String(user?.company?.id);
  }, [teamId, user?.company?.id]);

  // Mirrors requireSuperAdminFeature(PAYMENTGATEWAY) on the API: the ROOT super
  // admin is unlimited, everyone else is judged on the PaymentGateway feature.
  // Finance access deliberately does NOT grant this — the API never consulted
  // it, so using it here surfaced buttons the server then rejected with 403.
  // Step 1 of the API gate is the tenant check: payment gateways belong to the
  // Grand Parent company, and a non-GP login is rejected whatever its role says.
  // Role edits replicate cms_access down the hierarchy, so a company-level user
  // can hold a PaymentGateway grant the server will never honour.
  const isAuthorized = isGrandParent && (isRootSuperAdmin || paymentGatewayViewAccess);
  const createAccess =
    isGrandParent &&
    (isRootSuperAdmin || paymentGatewayCreateAccess) &&
    isGrandParentCompanySelected;
  const notAuthorized = !isAuthorized;
  const isReadOnly = isAuthorized && !createAccess;

  // A limited super admin can hold PaymentGateway create in their role and still
  // land here read-only because a child company is selected in the sidebar. Say
  // so, instead of the blanket "Not Authorized" that reads like a missing grant.
  const deniedMessage =
    isGrandParent &&
    (isRootSuperAdmin || paymentGatewayCreateAccess) &&
    !isGrandParentCompanySelected
      ? "Select the Grand Parent company to manage payment gateways"
      : "Not Authorized";

  useEffect(() => {
    if (
      (isCreatePaymentGateway && !createAccess) ||
      (!isCreatePaymentGateway && notAuthorized)
    ) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: deniedMessage })
      );
      navigate("/dashboard");
    }
  }, [dispatch, isCreatePaymentGateway, createAccess, navigate, notAuthorized, deniedMessage]);

  return (
    <Page>
      <BackButton
        text={
          isCreatePaymentGateway
            ? "Create Payment Gateway"
            : isReadOnly
            ? "Payment Gateway Details"
            : "Edit Payment Gateway Details"
        }
        path="/payment-gateways"
      />
      {isCreatePaymentGateway ? (
        <PaymentGatewayForm />
      ) : (
        <>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="Details" value="details" />
            <Tab label="Transactions" value="transactions" />
          </Tabs>
          {activeTab === "details" ? (
            <PaymentGatewayForm />
          ) : (
            <PaymentGatewayTransactions id={id} />
          )}
        </>
      )}
    </Page>
  );
};

